import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import '../../styles/ChildProfilePage.css';
import '../../styles/ChildAssignmentCardFix.css';

const ChildProfilePage = () => {
  const { childId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // State for child data and loading status
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for child's data
  const [assignments, setAssignments] = useState({ active: [], completed: [] });
  const [stats, setStats] = useState({
    assignments: { completionRate: 0 },
    attendance: { rate: 0, present: 0, total: 0 },
    ratings: { average: 0 }
  });
  
  // Use childId from params or from location state
  const effectiveChildId = childId || location.state?.childId;
  const childName = location.state?.childName;
  
  // Fetch child details and progress data
  useEffect(() => {
    if (!effectiveChildId) {
      setError('No child ID provided');
      setLoading(false);
      return;
    }
    
    const fetchChildData = async () => {
      setLoading(true);
      console.log('ChildProfilePage: Fetching data for child ID:', effectiveChildId);
      
      try {
        // Fetch child details first
        const childResponse = await api.get(`/users/${effectiveChildId}`);
        console.log('ChildProfilePage: Child details response:', childResponse.data);
        
        if (childResponse.data) {
          setChild(childResponse.data);
        } else {
          console.warn('ChildProfilePage: No child data returned from API');
        }
        
        // Fetch assignments data
        console.log('ChildProfilePage: Fetching assignments for child');
        const assignmentsResponse = await api.get(`/parents/children/${effectiveChildId}/assignments`);
        console.log('ChildProfilePage: Assignments response:', assignmentsResponse.data);
        
        if (assignmentsResponse.data && assignmentsResponse.data.data) {
          const assignmentsData = assignmentsResponse.data.data;
          console.log(`ChildProfilePage: Found ${assignmentsData.length} assignments`);
          
          // Filter active assignments - ANY assignment that's active OR needs repeat
          const activeAssignments = assignmentsData.filter(assignment => 
            assignment.status === 'active' || 
            (assignment.assessment && assignment.assessment.rating === 'Need to Repeat')
          );
          
          // Filter completed assignments - ONLY assignments that are completed status AND don't need repeat
          const completedAssignments = assignmentsData.filter(assignment => 
            assignment.status === 'completed' && 
            (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
          ).slice(0, 5); // Only show 5 most recent completed assignments
          
          setAssignments({
            active: activeAssignments,
            completed: completedAssignments
          });
          
          // Calculate assignment metrics
          const totalAssignments = assignmentsData.length;
          const completedCount = assignmentsData.filter(assignment => 
            assignment.status === 'completed' &&
            (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
          ).length;
          
          const completionRate = totalAssignments > 0 
            ? Math.round((completedCount / totalAssignments) * 100) 
            : 0;
          
          // Calculate assessment average
          const assessedAssignments = assignmentsData.filter(assignment => 
            assignment.assessment && assignment.assessment.rating
          );
          
          let assessmentAvgValue = 0;
          
          if (assessedAssignments.length > 0) {
            // Map ratings to numerical values
            const ratingValues = {
              'Excellent': 5,
              'Very Good': 4,
              'Good': 3,
              'Pass': 2,
              'Need to Repeat': 1
            };
            
            const assessmentSum = assessedAssignments.reduce((sum, assignment) => {
              return sum + (ratingValues[assignment.assessment.rating] || 0);
            }, 0);
            
            assessmentAvgValue = assessmentSum / assessedAssignments.length;
          }
          
          // Update assignment stats
          setStats(prev => ({
            ...prev,
            assignments: { 
              completionRate,
              total: totalAssignments,
              completed: completedCount
            },
            ratings: { 
              average: assessmentAvgValue > 0 ? parseFloat(assessmentAvgValue.toFixed(1)) : 0
            }
          }));
        }
        
        // Fetch attendance data
        console.log('ChildProfilePage: Fetching attendance for child');
        const attendanceResponse = await api.get(`/parents/children/${effectiveChildId}/attendance`);
        console.log('ChildProfilePage: Attendance response:', attendanceResponse.data);
        
        if (attendanceResponse.data && attendanceResponse.data.stats) {
          const attendanceStats = attendanceResponse.data.stats;
          console.log('ChildProfilePage: Attendance stats:', attendanceStats);
          
          // Update attendance stats
          setStats(prev => ({
            ...prev,
            attendance: { 
              rate: attendanceStats.attendanceRate || 0,
              present: attendanceStats.presentDays || 0,
              total: attendanceStats.totalDays || 0
            }
          }));
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching child data:', err);
        setError('Failed to load child data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchChildData();
  }, [effectiveChildId]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'No due date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'No due date';
    }
  };
  
  // Get status label similar to mobile app
  const getStatusLabel = (status) => {
    if (!status) return 'Pending';
    
    const statusLower = status.toLowerCase().replace(' ', '-');
    switch (statusLower) {
      case 'excellent': return 'Excellent';
      case 'very-good': return 'Very Good';
      case 'good': return 'Good';
      case 'pass': return 'Pass';
      case 'need-repeat': 
      case 'need-to-repeat': return 'Need to Repeat';
      case 'pending': return 'Pending Assessment';
      case 'needs-improvement': return 'Needs Improvement';
      default: return 'Pending';
    }
  };
  
  // Navigate to assignment details
  const navigateToAssignment = (assignment) => {
    // Use the student-view route which has the appropriate permissions for parents and students
    navigate(`/assignments/student-view/${assignment._id}`, {
      state: {
        fromParentChildView: true,
        childId: effectiveChildId,
        childName: child?.name || childName
      }
    });
  };
  
  // Navigate to all assignments
  const viewAllAssignments = () => {
    // Check if child is a student (has student role)
    if (child?.role === 'student') {
      // Navigate to StudentAssignmentsListPage and pass the student ID
      navigate(`/assignments/student`, {
        state: {
          fromParentChildView: true,
          childId: effectiveChildId,
          childName: child?.name || childName
        }
      });
    } else {
      // Fall back to the parent-specific assignments page if not a student
      navigate(`/parent/children/${effectiveChildId}/assignments`, {
        state: {
          fromParentChildView: true,
          childId: effectiveChildId,
          childName: child?.name || childName
        }
      });
    }
  };
  
  // Navigate to attendance
  const viewAttendance = () => {
    navigate(`/parent/children/${effectiveChildId}/attendance`, {
      state: {
        childId: effectiveChildId,
        childName: child?.name || childName
      }
    });
  };
  
  if (loading) {
    return (
      <div className="child-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading child profile...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="child-profile-error">
        <p>{error}</p>
        <button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </button>
      </div>
    );
  }
  
  const childDisplayName = child?.name || childName || 'Child';
  
  return (
    <div className="child-profile-container">
      <div className="child-profile-header">
        <Link to="/dashboard" className="back-link">
          ← Back to Parent Dashboard
        </Link>
        <h1>{childDisplayName}'s Profile</h1>
      </div>
      
      {/* Stats Overview */}
      <div className="child-stats-overview">
        <div className="stat-card assignments-stat">
          <h3>Assignments</h3>
          <div className="stat-value">{stats.assignments.completionRate}%</div>
          <div className="stat-details">
            {stats.assignments.completed} of {stats.assignments.total} completed
          </div>
        </div>
        
        <div className="stat-card ratings-stat">
          <h3>Average Rating</h3>
          <div className="stat-value">{stats.ratings.average}/5</div>
          <div className="stat-details">
            Based on {assignments.completed.length} assessed assignments
          </div>
        </div>
        
        <div className="stat-card attendance-stat">
          <h3>Attendance</h3>
          <div className="stat-value">{stats.attendance.rate}</div>
          <div className="stat-details">
            Present: {stats.attendance.present}/{stats.attendance.total} days
          </div>
        </div>
      </div>
      
      {/* Current Assignments */}
      <div className="assignments-section">
        <div className="section-header">
          <h2>Current Assignments</h2>
          <button 
            className="view-all-btn"
            onClick={viewAllAssignments}
          >
            View All
          </button>
        </div>
        
        {assignments.active.length > 0 ? (
          <div className="assignments-list">
            {assignments.active.map(assignment => (
              <div 
                key={assignment._id} 
                className="assignment-card"
                onClick={() => navigateToAssignment(assignment)}
              >
                <div className="assignment-type-badge">
                  {assignment.type === 'new-memorization' ? 'New Memorization' : 'Revision'}
                </div>
                {/* Make sure title is properly displayed on both desktop and mobile */}
                <h3 className="assignment-title">
                  {assignment.surah?.name || 'Unknown Surah'} {assignment.verses ? `(${assignment.verses.from}-${assignment.verses.to})` : ''}
                </h3>
                <div className="assignment-due-date">
                  Due: {formatDate(assignment.dueDate)}
                </div>
                <div className={`assignment-status ${assignment.assessment ? 'has-assessment' : ''}`}>
                  {assignment.assessment && assignment.assessment.rating
                    ? `Assessment: ${assignment.assessment.rating}` 
                    : 'Status: Pending Assessment'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-assignments">
            <p>No current assignments</p>
          </div>
        )}
      </div>
      
      
      {/* Attendance Summary */}
      <div className="attendance-section">
        <div className="section-header">
          <h2>Attendance Summary</h2>
          <button 
            className="view-all-btn"
            onClick={viewAttendance}
          >
            View Full Record
          </button>
        </div>
        
        <div className="attendance-summary">
          <div className="attendance-stats">
            <div className="attendance-stat">
              <span className="stat-label">Present:</span>
              <span className="stat-value">{stats.attendance.present} days</span>
            </div>
            <div className="attendance-stat">
              <span className="stat-label">Absent:</span>
              <span className="stat-value">{stats.attendance.total - stats.attendance.present} days</span>
            </div>
            <div className="attendance-stat">
              <span className="stat-label">Attendance Rate:</span>
              <span className="stat-value">{stats.attendance.rate}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildProfilePage;